/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, ReactNode } from "react";

import { MenuItem } from "primereact/menuitem";

import { useBreadcrumbContextOptional } from "../Breadcrumb/BreadcrumbProvider";
import type { NavContextType } from "./types";

const NavContext = createContext<NavContextType | undefined>(undefined);

interface NavContextProviderProps {
  children: ReactNode;
}

/**
 * @deprecated Use BreadcrumbProvider instead. NavContextProvider is kept for backward compatibility.
 *
 * NavContextProvider
 *
 * Provides a context for managing navigation breadcrumbs throughout the application.
 * Components can use this to override or supply breadcrumbs that will be displayed
 * in the UserNavBar.
 *
 * @param {NavContextProviderProps} props - The provider props
 * @returns {JSX.Element} The provider component
 */
export const NavContextProvider: React.FC<NavContextProviderProps> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<MenuItem[] | undefined>(undefined);

  const value: NavContextType = {
    breadcrumbs,
    setBreadcrumbs,
  };

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
};

/**
 * useNavContext Hook
 *
 * Custom hook to access the navigation context. Provides methods to get and set
 * breadcrumbs for the navigation bar.
 *
 * If a BreadcrumbProvider is present in the tree, this hook delegates to it
 * for backward compatibility. Otherwise falls back to NavContextProvider or
 * a no-op default.
 *
 * @returns {NavContextType} The navigation context value
 *
 * @example
 * ```tsx
 * const { breadcrumbs, setBreadcrumbs } = useNavContext();
 *
 * // Set breadcrumbs in a component
 * useEffect(() => {
 *   setBreadcrumbs([
 *     { label: 'Home', command: () => navigate('/') },
 *     { label: 'Current Page' }
 *   ]);
 *
 *   return () => setBreadcrumbs(undefined); // Clear on unmount
 * }, []);
 * ```
 */
export const useNavContext = (): NavContextType => {
  // Always call both hooks unconditionally to satisfy React rules-of-hooks
  const breadcrumbCtx = useBreadcrumbContextOptional();
  const navContext = useContext(NavContext);

  // Prefer BreadcrumbProvider if available (new system)
  if (breadcrumbCtx) {
    return {
      breadcrumbs: breadcrumbCtx.breadcrumbs,
      setBreadcrumbs: breadcrumbCtx.setBreadcrumbs,
    };
  }

  // Fall back to NavContext (legacy)
  if (navContext) {
    return navContext;
  }

  // No provider available — return no-op defaults
  return {
    setBreadcrumbs: () => {},
    breadcrumbs: undefined,
  };
};
